import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, Button, Card, Field, apiErrorMessage, brandDisplayName, PillButton } from '@aralere/shared-ui';
import type {
  AdminNotificationView,
  AdminNotificationStatus,
  CreateNotificationRequest,
  UpdateNotificationRequest,
} from '@aralere/shared-types';

// Admin notification authoring (spec §12 SYSTEM, 08 Q25). Management list of ALL notifications with
// create / edit / set-extend-expiry / remove for BROADCASTS. Invoice/payment reminders are
// system-generated (Stage-4/5 billing) and listed read-only — the server 403s an edit/remove of a
// non-broadcast row, so the UI doesn't offer those controls for them. Trail = auditLog notification.*
// (written server-side). Nav-gated by adminSurface + RequireRole('platform-admin') in App.tsx.

// ── datetime-local ⇄ ISO (browser-local tz; minute precision) ──────────────────
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local: string): string | null {
  if (!local.trim()) return null;
  const d = new Date(local); // interpreted in the browser's local zone
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

type AudienceOptions = {
  businesses: ReadonlyArray<{ id: string; name: string }>;
  clients: ReadonlyArray<{ id: string; name: string; active: boolean; clientType: 'business' | 'consumer' }>;
};

// Main-dropdown sentinel for "the audience is a specific client" (only shown when one is picked).
const CLIENT_SENTINEL = '__specific-client__';

function audienceLabel(audience: string, opts: AudienceOptions): string {
  if (audience === 'all') return 'Everyone';
  if (audience === 'admin') return 'Operators';
  if (audience.startsWith('user-')) return 'A specific person';
  const biz = opts.businesses.find((b) => b.id === audience);
  if (biz) return `Brand · ${biz.name}`;
  const cl = opts.clients.find((c) => c.id === audience);
  if (cl) return `Client · ${cl.name}`;
  return audience;
}

const STATUS_PILL: Record<AdminNotificationStatus, string> = {
  active: 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]',
  scheduled: 'bg-[var(--tint-blue-bg)] text-[var(--tint-blue-text)]',
  expired: 'bg-[rgb(var(--edge-rgb)/0.10)] text-[var(--color-muted)]',
};

const STATUS_LABEL: Record<AdminNotificationStatus, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  expired: 'Expired',
};

const TYPE_LABEL: Record<string, string> = {
  broadcast: 'Broadcast',
  invoice: 'Invoice',
  'payment-reminder': 'Payment reminder',
  'service-assigned': 'Service assigned',
};

type FormState = {
  audience: string;
  headline: string;
  body: string;
  postDate: string; // datetime-local or ''
  expiresAt: string; // datetime-local or ''
  linkTo: string; // relative in-app path or ''
};

const emptyForm = (): FormState => ({ audience: 'all', headline: '', body: '', postDate: '', expiresAt: '', linkTo: '' });

function formFromNotification(n: AdminNotificationView): FormState {
  return {
    audience: n.audience,
    headline: n.headline,
    body: n.body,
    postDate: isoToLocalInput(n.postDate),
    expiresAt: isoToLocalInput(n.expiresAt),
    linkTo: n.linkTo ?? '',
  };
}

function buildCreate(f: FormState): CreateNotificationRequest {
  return {
    audience: f.audience,
    headline: f.headline.trim(),
    body: f.body.trim(),
    postDate: localInputToIso(f.postDate), // null → server posts now
    expiresAt: localInputToIso(f.expiresAt), // null → never expires
    linkTo: f.linkTo.trim() || null, // null → no deep-link
  };
}

/** Minimal PATCH: only fields the admin actually changed (compared in the local-input form so an
 *  untouched datetime field — minute precision — doesn't read as a spurious change). */
function buildUpdate(f: FormState, orig: AdminNotificationView): UpdateNotificationRequest {
  const patch: UpdateNotificationRequest = {};
  if (f.headline.trim() !== orig.headline) patch.headline = f.headline.trim();
  if (f.body.trim() !== orig.body) patch.body = f.body.trim();
  if (f.postDate !== isoToLocalInput(orig.postDate)) patch.postDate = localInputToIso(f.postDate);
  if (f.expiresAt !== isoToLocalInput(orig.expiresAt)) patch.expiresAt = localInputToIso(f.expiresAt);
  if (f.linkTo.trim() !== (orig.linkTo ?? '')) patch.linkTo = f.linkTo.trim() || null;
  return patch;
}

export function AdminNotifications(): JSX.Element {
  const [notifications, setNotifications] = useState<AdminNotificationView[] | null>(null);
  const [audienceOptions, setAudienceOptions] = useState<AudienceOptions>({ businesses: [], clients: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminNotificationView | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [notifs, biz, cl] = await Promise.all([
        api.admin.notifications.list(),
        api.admin.listBusinesses(),
        api.admin.listClients(),
      ]);
      setNotifications(notifs.notifications);
      setAudienceOptions({
        businesses: biz.businesses.map((b) => ({ id: b.id, name: brandDisplayName(b.name) })),
        clients: cl.clients.map((r) => ({
          id: r.client.id,
          name: r.client.displayName,
          active: r.client.status === 'active',
          clientType: r.client.clientType,
        })),
      });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (formOpen) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [formOpen]);

  const update = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm());
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (n: AdminNotificationView): void => {
    setEditing(n);
    setForm(formFromNotification(n));
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setEditing(null);
    setFormError(null);
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    if (form.headline.trim() === '') {
      setFormError('A headline is required.');
      return;
    }
    const link = form.linkTo.trim();
    if (link !== '' && (!link.startsWith('/') || link.startsWith('//') || link.includes('://'))) {
      setFormError('Link must be a relative in-app path beginning with “/” (e.g. /services); no external URLs.');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        const patch = buildUpdate(form, editing);
        if (Object.keys(patch).length > 0) await api.admin.notifications.update(editing.id, patch);
      } else {
        await api.admin.notifications.create(buildCreate(form));
      }
      await load();
      closeForm();
    } catch (err) {
      setFormError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const expireNow = async (n: AdminNotificationView): Promise<void> => {
    if (!window.confirm(`Expire “${n.headline}” now?\n\nIt immediately stops showing in clients' bells. You can't un-expire it, but the record stays in this list.`))
      return;
    setBusyId(n.id);
    setError(null);
    try {
      await api.admin.notifications.update(n.id, { expiresAt: new Date().toISOString() });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const removeRow = async (n: AdminNotificationView): Promise<void> => {
    if (!window.confirm(`Remove “${n.headline}” permanently?\n\nThis deletes the broadcast outright (not just expires it). This can't be undone.`))
      return;
    setBusyId(n.id);
    setError(null);
    try {
      await api.admin.notifications.remove(n.id);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const list = notifications ?? [];

  // Audience picker (create only). Three independent controls, each rendered ONLY when it has
  // something to offer — so this composer degrades gracefully for a SaaS tenant:
  //   • Main "Audience": Everyone + each brand, plus a "Specific client" sentinel shown only while a
  //     client is selected. Brand options appear ONLY when there's >1 brand, so a single-brand SaaS
  //     instance shows just "Everyone" here.
  //   • "Specific business client" + "Specific consumer client": two SEPARATE dropdowns, each shown
  //     only when that client type has active clients — so a tenant collapses to two, one, or zero
  //     client dropdowns automatically.
  // The audience TOKENS (all / businessId / clientId) are unchanged; this is purely how the UI
  // adapts to what exists. Picking a client in either dropdown sets the audience (the other dropdown
  // clears, since its selected value no longer matches); clearing the one holding the selection
  // reverts to Everyone.
  const businessIdSet = new Set(audienceOptions.businesses.map((b) => b.id));
  const multiBrand = audienceOptions.businesses.length > 1;
  const activeClients = audienceOptions.clients.filter((c) => c.active);
  const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name);
  const businessClients = activeClients.filter((c) => c.clientType === 'business').sort(byName);
  const consumerClients = activeClients.filter((c) => c.clientType === 'consumer').sort(byName);
  const businessClientIdSet = new Set(businessClients.map((c) => c.id));
  const consumerClientIdSet = new Set(consumerClients.map((c) => c.id));
  const isClientAudience = form.audience !== 'all' && form.audience !== '' && !businessIdSet.has(form.audience);
  const mainAudienceValue =
    form.audience === 'all' || businessIdSet.has(form.audience) ? form.audience : CLIENT_SENTINEL;
  const selectedBusinessClientId =
    isClientAudience && businessClientIdSet.has(form.audience) ? form.audience : '';
  const selectedConsumerClientId =
    isClientAudience && consumerClientIdSet.has(form.audience) ? form.audience : '';
  const onMainAudienceChange = (v: string): void => {
    if (v === CLIENT_SENTINEL) return; // only reachable when a client is already chosen → no-op
    update({ audience: v }); // 'all' or a brand — drops any specific-client selection
  };
  const onBusinessClientChange = (v: string): void => {
    if (v === '') {
      if (selectedBusinessClientId) update({ audience: 'all' }); // cleared the business selection → Everyone
    } else {
      update({ audience: v }); // a business clientId → main switches to "Specific client"
    }
  };
  const onConsumerClientChange = (v: string): void => {
    if (v === '') {
      if (selectedConsumerClientId) update({ audience: 'all' }); // cleared the consumer selection → Everyone
    } else {
      update({ audience: v }); // a consumer clientId → main switches to "Specific client"
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Notifications</h1>
          <p className="text-sm text-[var(--color-muted)] max-w-2xl">
            Broadcast announcements shown to clients in the notification bell. A broadcast clears for each
            person 72h after they first open the bell, or at an expiry you set, whichever comes first. Invoice
            and payment reminders are generated by the billing system and listed here read-only.
          </p>
        </div>
        <Button onClick={openCreate}>New broadcast</Button>
      </header>

      {formOpen ? (
        <div ref={formRef}>
          <Card>
            <form onSubmit={submit} className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl">{editing ? 'Edit broadcast' : 'New broadcast'}</h2>
                {editing ? <span className="text-xs text-[var(--color-muted)] font-mono">{editing.id}</span> : null}
              </div>

              {editing ? (
                <Field label="Audience">
                  <div className="text-sm rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] px-3 py-2">
                    {audienceLabel(editing.audience, audienceOptions)}
                    <span className="text-xs text-[var(--color-muted)]"> · can't be changed after posting</span>
                  </div>
                </Field>
              ) : (
                <div className="space-y-3">
                  <Field label="Audience" required>
                    <select
                      value={mainAudienceValue}
                      onChange={(e) => onMainAudienceChange(e.target.value)}
                      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                    >
                      <option value="all">Everyone (all clients{multiBrand ? ' & brands' : ''})</option>
                      {multiBrand
                        ? audienceOptions.businesses.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))
                        : null}
                      {isClientAudience ? <option value={CLIENT_SENTINEL}>Specific client</option> : null}
                    </select>
                  </Field>
                  {businessClients.length > 0 ? (
                    <Field label="Specific business client (optional)">
                      <select
                        value={selectedBusinessClientId}
                        onChange={(e) => onBusinessClientChange(e.target.value)}
                        className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                      >
                        <option value="">(none)</option>
                        {businessClients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}
                  {consumerClients.length > 0 ? (
                    <Field label="Specific consumer client (optional)">
                      <select
                        value={selectedConsumerClientId}
                        onChange={(e) => onConsumerClientChange(e.target.value)}
                        className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                      >
                        <option value="">(none)</option>
                        {consumerClients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}
                  {businessClients.length > 0 || consumerClients.length > 0 ? (
                    <p className="text-xs text-[var(--color-muted)]">
                      Pick a client to send only to them; the audience above switches to “Specific client.”
                    </p>
                  ) : null}
                </div>
              )}

              <Field label="Headline" required>
                <input
                  value={form.headline}
                  onChange={(e) => update({ headline: e.target.value })}
                  placeholder="Scheduled maintenance this weekend"
                  required
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                />
              </Field>

              <Field label="Body (optional)">
                <textarea
                  value={form.body}
                  onChange={(e) => update({ body: e.target.value })}
                  rows={3}
                  placeholder="Portal access may be briefly interrupted between 1–3am Saturday."
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                />
              </Field>

              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Post date (optional)">
                  <input
                    type="datetime-local"
                    value={form.postDate}
                    onChange={(e) => update({ postDate: e.target.value })}
                    className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  />
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Blank posts immediately. A future time schedules it.
                  </p>
                </Field>
                <Field label="Expires (optional)">
                  <input
                    type="datetime-local"
                    value={form.expiresAt}
                    onChange={(e) => update({ expiresAt: e.target.value })}
                    className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  />
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Blank never auto-expires (clients still auto-clear 72h after first opening the bell).
                  </p>
                </Field>
              </div>

              <Field label="Link (optional)">
                <input
                  value={form.linkTo}
                  onChange={(e) => update({ linkTo: e.target.value })}
                  placeholder="/services"
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Makes the whole notification clickable, opening this in-app page. Use a relative path
                  like <span className="font-mono">/services</span>; external URLs aren’t allowed.
                </p>
              </Field>

              {formError ? (
                <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)]">
                  {formError}
                </div>
              ) : null}

              <div className="flex gap-3 pt-1">
                <Button type="submit" loading={submitting} disabled={submitting}>
                  {editing ? 'Save changes' : 'Post broadcast'}
                </Button>
                <Button type="button" variant="ghost" onClick={closeForm} disabled={submitting}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {error ? <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p> : null}
      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {notifications && list.length === 0 && !loading ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            No notifications yet. Click <span className="font-medium">New broadcast</span> to post the first
            announcement.
          </p>
        </Card>
      ) : null}

      {list.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] glass-flat">
          <table className="w-full text-sm">
            <thead className="bg-[rgb(var(--edge-rgb)/0.05)] text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Notification</th>
                <th className="px-4 py-3 font-medium">Audience</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Posted</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
              {list.map((n) => {
                const isBroadcast = n.type === 'broadcast';
                return (
                  <tr key={n.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{n.headline}</div>
                      {n.body ? (
                        <div className="text-xs text-[var(--color-muted)] line-clamp-2 max-w-md">{n.body}</div>
                      ) : null}
                      {!isBroadcast ? (
                        <div className="mt-0.5 text-[11px] uppercase tracking-wide text-[var(--color-muted)]">
                          {TYPE_LABEL[n.type] ?? n.type}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-muted)]">{audienceLabel(n.audience, audienceOptions)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[n.status]}`}
                      >
                        {STATUS_LABEL[n.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-muted)] whitespace-nowrap">{formatDateTime(n.postDate)}</td>
                    <td className="px-4 py-3 text-[var(--color-muted)] whitespace-nowrap">{formatDateTime(n.expiresAt)}</td>
                    <td className="px-4 py-3">
                      {isBroadcast ? (
                        <div className="flex justify-end gap-2">
                          <PillButton onClick={() => openEdit(n)} disabled={busyId === n.id}>
                            Edit
                          </PillButton>
                          {n.status !== 'expired' ? (
                            <PillButton onClick={() => void expireNow(n)} disabled={busyId === n.id}>
                              Expire
                            </PillButton>
                          ) : null}
                          <PillButton tone="red" onClick={() => void removeRow(n)} disabled={busyId === n.id}>
                            Remove
                          </PillButton>
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <span className="text-xs text-[var(--color-muted)] italic">System-managed</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

// ── local helpers (match the Services/Settings editor idiom) ───────────────────
